import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { useDispatch, useSelector } from 'react-redux';
import { clearSuccess } from '~/actions/success';
import { AvatarListProxy } from '~/commonComponents/AvatarList';
import { useIsSuccess } from '~/hooks';
import { UPLOAD_AVATAR_ACTION, uploadAvatar } from '../../../actions';
import { createLoadingSelector } from '../../../api/selectors';
import { Button } from '@tempmee/react';
import { ImageCropper } from '../../../commonComponents/ImageCropper';
import Portal from '../../../commonComponents/Portal';

import OfficeAvatar1 from '../../../images/avatars/Office-Avatar-1.svg';
import { Colors } from '../../../themes/colors';
import { getBlobFromBlobUrl } from '../../../utils/Blob';
import { OutlineXIcon } from '@tempmee/react-icons';

const actions = [UPLOAD_AVATAR_ACTION];
const loadingSelector = createLoadingSelector([UPLOAD_AVATAR_ACTION]);

export const ChangeAccountPhotoModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.selectedChildOffice ?? state.user.user);
  const isLoading = useSelector((state) => loadingSelector(state));
  const { isSuccess: isSuccessUploadAvatar } = useIsSuccess(actions);
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [fullImage, setFullImage] = useState(null);
  const [showCropperModal, setShowCropperModal] = useState(false);
  const { getInputProps, open } = useDropzone({
    accept: { 'image/*': [] },
    maxFiles: 1,
    noClick: true,
    maxSize: 2097152,
    noKeyboard: true,
    onDrop: (acceptedFiles) => {
      const files = acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        }),
      );
      setFullImage(files[0]);
      setShowCropperModal(true);
    },
  });

  const handleAvatarSelect = (img, index) => {
    if (selectedIcon === index) {
      setSelectedIcon(null);
      setSelectedImage(null);
      return;
    }

    setSelectedImage(img);
    setSelectedIcon(index);
  };

  const onCropComplete = async (_croppedImage) => {
    saveImage(_croppedImage);
  };

  const saveImage = async (img) => {
    dispatch(uploadAvatar({ avatar: await getBlobFromBlobUrl(img) }));
  };

  useEffect(() => {
    if (isSuccessUploadAvatar) {
      onClose();
      dispatch(clearSuccess(actions));
    }
  }, [isSuccessUploadAvatar]);

  return (
    <>
      {!showCropperModal ? (
        <Portal>
          <div
            className="modal"
            style={{
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              position: 'fixed',
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              style={{
                width: 600,
                backgroundColor: colors.shades[0],
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                borderRadius: 14,
                padding: 35,
                paddingBottom: 48,
                overflowY: 'auto',
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <OutlineXIcon />

              <span
                style={{
                  fontFamily: 'Nunito',
                  fontSize: 28,
                  fontWeight: 700,
                  color: colors.neutral[600],
                }}
              >
                Change Account Photo
              </span>

              <img
                alt="profile"
                src={user?.profile_image ?? OfficeAvatar1}
                style={{
                  width: 120,
                  height: 120,
                  borderRadius: '100%',
                  resizeMode: 'contain',
                  marginTop: 28,
                  marginBottom: 24,
                }}
              />

              <input {...getInputProps()} />
              <Button variant="secondary" size="md" onClick={open}>
                Upload from Computer
              </Button>

              <Typography italic color={colors.neutral[500]} style={{ marginTop: 16 }}>
                (Maximum upload file size 2MB)
              </Typography>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 12,
                  width: 400,
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginTop: 24,
                  marginBottom: 24,
                }}
              >
                <div
                  style={{
                    height: 2,
                    flex: 1,
                    backgroundColor: Colors.neutral_100,
                  }}
                />
                <Typography variant="subtitle1" italic color={colors.neutral[600]}>
                  or
                </Typography>
                <div
                  style={{
                    height: 2,
                    flex: 1,
                    backgroundColor: Colors.neutral_100,
                  }}
                />
              </div>

              <AvatarListProxy onSelect={handleAvatarSelect} selected={selectedIcon} horizontal />

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 16,
                  marginTop: 24,
                }}
              >
                <Button variant="secondary" size="md" onClick={onClose}>
                  Go Back
                </Button>
                <Button
                  size="md"
                  isLoading={isLoading}
                  disabled={!selectedIcon}
                  onClick={() => saveImage(selectedImage)}
                >
                  Select
                </Button>
              </div>
            </motion.div>
          </div>
        </Portal>
      ) : (
        <ImageCropper
          src={URL.createObjectURL(fullImage)}
          onComplete={onCropComplete}
          onClose={onClose}
          leftButtonText="Go Back"
          leftButtonOnClick={() => setShowCropperModal(false)}
          rightButtonLoading={isLoading}
        />
      )}
    </>
  );
};
